import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { tasksApi, Task } from '../api/tasks';
import { leadsApi } from '../api/leads';
import { accountsApi } from '../api/accounts';
import { notificationsApi } from '../api/notifications';
import { useAuth } from '../context/AuthContext';
import PremiumLayout from '../components/PremiumLayout';
import { 
  PlusIcon, 
  CheckCircleIcon, 
  ClockIcon, 
  XCircleIcon, 
  PencilIcon, 
  TrashIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  LinkIcon,
  FunnelIcon,
  XMarkIcon,
  CalendarIcon,
  ArrowTopRightOnSquareIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

interface ClientGroup {
  id: string;
  name: string;
  type: 'account' | 'lead';
  entityId: number;
  tasks: any[];
  lastTaskDate: string;
  taskCount: number;
  leadStage?: string;
}

type TaskOriginFilter = 'all' | 'human' | 'agent';
const SYSTEM_CLOSE_PLAN_TASK_PATTERN = /^create close plan for\b/i;

function isSystemClosePlanTask(title?: string | null): boolean {
  return !!title && SYSTEM_CLOSE_PLAN_TASK_PATTERN.test(title.trim());
}

const Tasks: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [selectedClient, setSelectedClient] = useState<ClientGroup | null>(null);
  const [highlightedTaskId, setHighlightedTaskId] = useState<number | null>(null);
  const [notificationIdToClear, setNotificationIdToClear] = useState<number | null>(null);
  const taskRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const [filters, setFilters] = useState({
    origin: 'all' as TaskOriginFilter,
    status: 'all' as 'all' | 'pending' | 'in_progress' | 'completed' | 'cancelled',
    priority: 'all' as 'all' | 'low' | 'medium' | 'high' | 'urgent',
    urgency: 'all' as 'all' | 'critical' | 'high' | 'medium' | 'low',
    searchTerm: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'pending',
    priority: 'medium',
    due_date: '',
    completed_at: '',
    lead_id: '',
    account_id: '',
  });

  const queryClient = useQueryClient();
  const locationState = location.state as
    | { highlightTaskId?: number; scrollToTask?: boolean; notificationId?: number }
    | null;

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['tasks', filters.origin],
    queryFn: () => tasksApi.getByOwnerType(filters.origin === 'all' ? undefined : filters.origin),
  });

  const { data: leads = [] } = useQuery({
    queryKey: ['leads'],
    queryFn: leadsApi.getAll,
  });

  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts'],
    queryFn: accountsApi.getAll,
  });

  const { data: notifications = [] } = useQuery({
    queryKey: ['notifications', user?.id, 'tasks-page'],
    queryFn: () => notificationsApi.getAll(false, 200),
    enabled: !!user?.id,
    refetchInterval: 30000,
  });

  // Calculate urgency for each task
  const tasksWithUrgency = useMemo(() => {
    return tasks
      .filter(task => !isSystemClosePlanTask(task.title))
      .map(task => {
      let urgency: 'critical' | 'high' | 'medium' | 'low' = 'low';
      let urgencyReason = '';

      if (task.due_date) {
        const dueDate = new Date(task.due_date);
        const now = new Date();
        const hoursUntilDue = (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60);

        if (hoursUntilDue < 0) {
          urgency = 'critical';
          urgencyReason = 'Overdue';
        } else if (hoursUntilDue <= 24) {
          urgency = 'critical';
          urgencyReason = 'Due within 24 hours';
        } else if (hoursUntilDue <= 72) {
          urgency = 'high';
          urgencyReason = 'Due within 3 days';
        } else if (hoursUntilDue <= 168) {
          urgency = 'medium';
          urgencyReason = 'Due within a week';
        } else {
          urgency = 'low';
          urgencyReason = 'Due later';
        }
      } else if (task.priority === 'urgent') {
        urgency = 'high';
        urgencyReason = 'Urgent priority';
      } else if (task.priority === 'high') {
        urgency = 'medium';
        urgencyReason = 'High priority';
      }

        return { ...task, urgency, urgencyReason };
      });
  }, [tasks]);

  const taskNotificationMap = useMemo(() => {
    const map = new Map<number, number[]>();
    notifications
      .filter((notification) => notification.entity_type === 'task' && !!notification.entity_id)
      .forEach((notification) => {
        const taskId = Number(notification.entity_id);
        if (!map.has(taskId)) {
          map.set(taskId, []);
        }
        map.get(taskId)!.push(notification.id);
      });
    return map;
  }, [notifications]);

  // Filter tasks based on filters
  const filteredTasks = useMemo(() => {
    let filtered = [...tasksWithUrgency];

    // Filter by status
    if (filters.status !== 'all') {
      filtered = filtered.filter(t => t.status === filters.status);
    }

    // Filter by priority
    if (filters.priority !== 'all') {
      filtered = filtered.filter(t => t.priority === filters.priority);
    }

    // Filter by urgency
    if (filters.urgency !== 'all') {
      filtered = filtered.filter(t => t.urgency === filters.urgency);
    }

    // Filter by search term
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(t => 
        t.title.toLowerCase().includes(searchLower) ||
        t.description?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [tasksWithUrgency, filters]);

  // Group tasks by client (Lead or Account)
  const clientGroups = useMemo(() => {
    const groups: Map<string, ClientGroup> = new Map();

    filteredTasks.forEach(task => {
      let clientKey = '';
      let clientName = '';
      let clientType: 'account' | 'lead' = 'lead';
      let entityId = 0;
      let leadStage = '';

      if (task.lead_id) {
        const lead = leads.find(l => l.id === task.lead_id);
        if (lead) {
          clientKey = `lead-${task.lead_id}`;
          // Get account name if available (company/account name is priority)
          const account = accounts.find(a => a.id === lead.account_id);
          const contactName = lead.first_name && lead.last_name
            ? `${lead.first_name} ${lead.last_name}`
            : lead.first_name || lead.lead_name || '';
          clientName = account?.name || lead.project_name || lead.brand || lead.name || contactName || 'Unknown Lead';
          clientType = 'lead';
          entityId = task.lead_id;
          leadStage = task.lead_stage || lead.stage || '';
        }
      } else if (task.account_id && task.account_name) {
        const account = accounts.find(a => a.id === task.account_id);
        clientKey = `account-${task.account_id}`;
        clientName = task.account_name;
        clientType = 'account';
        entityId = task.account_id;
      } else {
        // Standalone tasks
        clientKey = 'standalone';
        clientName = 'Standalone Tasks';
        clientType = 'lead';
        entityId = 0;
      }

      if (clientKey) {
        if (!groups.has(clientKey)) {
          groups.set(clientKey, {
            id: clientKey,
            name: clientName,
            type: clientType,
            entityId,
            tasks: [],
            lastTaskDate: task.due_date || task.created_at,
            taskCount: 0,
            leadStage,
          });
        }
        const group = groups.get(clientKey)!;
        group.tasks.push(task);
        group.taskCount = group.tasks.length;
        // Update last task date
        const taskDate = task.due_date || task.created_at;
        if (new Date(taskDate) > new Date(group.lastTaskDate)) {
          group.lastTaskDate = taskDate;
        }
      }
    });

    // Sort by last task date (most recent first)
    return Array.from(groups.values()).sort((a, b) => 
      new Date(b.lastTaskDate).getTime() - new Date(a.lastTaskDate).getTime()
    );
  }, [filteredTasks, leads, accounts]);

  // Get tasks for selected client
  const selectedClientTasks = useMemo(() => {
    if (!selectedClient) return [];
    return selectedClient.tasks.sort((a, b) => {
      // Sort by urgency first, then due date
      const urgencyOrder = { critical: 1, high: 2, medium: 3, low: 4 };
      const urgencyDiff = (urgencyOrder[a.urgency as keyof typeof urgencyOrder] || 4) - 
                         (urgencyOrder[b.urgency as keyof typeof urgencyOrder] || 4);
      if (urgencyDiff !== 0) return urgencyDiff;
      
      if (a.due_date && b.due_date) {
        return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      }
      if (a.due_date) return -1;
      if (b.due_date) return 1;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [selectedClient]);

  useEffect(() => {
    if (!selectedClient) {
      return;
    }

    const updatedClient = clientGroups.find((client) => client.id === selectedClient.id);
    setSelectedClient(updatedClient || null);
  }, [clientGroups, selectedClient]);

  useEffect(() => {
    if (!locationState?.highlightTaskId) {
      return;
    }

    setHighlightedTaskId(locationState.highlightTaskId);
    setNotificationIdToClear(locationState.notificationId ?? null);

    if (!locationState.scrollToTask) {
      return;
    }

    const scrollTimer = setTimeout(() => {
      const taskElement = taskRefs.current[locationState.highlightTaskId!];
      if (taskElement) {
        taskElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 250);

    return () => clearTimeout(scrollTimer);
  }, [locationState, clientGroups]);

  const createMutation = useMutation({
    mutationFn: tasksApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setShowModal(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Task> }) =>
      tasksApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setShowModal(false);
      setEditingTask(null);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: tasksApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (selectedClient) {
        const updated = clientGroups.find(c => c.id === selectedClient.id);
        if (updated) setSelectedClient(updated);
      }
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Failed to delete task';
      alert(message);
    },
  });

  const clearNotificationMutation = useMutation({
    mutationFn: notificationsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      setNotificationIdToClear(null);
    },
  });

  const clearTaskNotificationsMutation = useMutation({
    mutationFn: async (notificationIds: number[]) => {
      await Promise.all(notificationIds.map((id) => notificationsApi.delete(id)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      status: 'pending',
      priority: 'medium',
      due_date: '',
      completed_at: '',
      lead_id: '',
      account_id: '',
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const taskData: any = {
      title: formData.title,
      description: formData.description,
      status: formData.status,
      priority: formData.priority,
      due_date: formData.due_date || null,
      assigned_to: user?.id,
    };
    if (formData.status === 'completed') {
      taskData.completed_at = formData.completed_at || undefined;
    } else {
      taskData.completed_at = null;
    }
    if (formData.lead_id) {
      taskData.lead_id = parseInt(formData.lead_id);
    }
    if (formData.account_id) {
      taskData.account_id = parseInt(formData.account_id);
    }

    if (editingTask) {
      updateMutation.mutate({ id: editingTask.id, data: taskData });
    } else {
      createMutation.mutate(taskData);
    }
  };

  const handleEdit = (task: Task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      status: task.status,
      priority: task.priority || 'medium',
      due_date: task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : '',
      completed_at: task.completed_at ? new Date(task.completed_at).toISOString().split('T')[0] : '',
      lead_id: task.lead_id ? String(task.lead_id) : '',
      account_id: task.account_id ? String(task.account_id) : '',
    });
    setShowModal(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  };

  const handleClearNotification = () => {
    if (!notificationIdToClear) {
      return;
    }

    if (window.confirm('Clear the notification that opened this task?')) {
      clearNotificationMutation.mutate(notificationIdToClear);
    }
  };

  const handleClearTaskNotifications = (taskId: number) => {
    const notificationIds = taskNotificationMap.get(taskId) || [];
    if (notificationIds.length === 0) {
      return;
    }

    const message = notificationIds.length === 1
      ? 'Clear the notification for this task?'
      : `Clear ${notificationIds.length} notifications for this task?`;

    if (window.confirm(message)) {
      clearTaskNotificationsMutation.mutate(notificationIds);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'in_progress':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'cancelled':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'critical':
        return 'bg-red-500 text-white';
      case 'high':
        return 'bg-orange-500 text-white';
      case 'medium':
        return 'bg-yellow-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  const getOwnerTypeMeta = (ownerType?: string | null) => {
    if (ownerType === 'human') {
      return {
        label: 'Human',
        className: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      };
    }

    return {
      label: 'AI',
      className: 'bg-sky-100 text-sky-800 border-sky-200',
    };
  };

  const getClientIcon = (type: 'account' | 'lead') => {
    switch (type) {
      case 'account':
        return <BuildingOfficeIcon className="h-5 w-5 text-indigo-500" />;
      case 'lead':
        return <ChartBarIcon className="h-5 w-5 text-orange-500" />;
    }
  };

  if (isLoading) {
    return (
      <PremiumLayout title="Tasks">
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
        </div>
      </PremiumLayout>
    );
  }

  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const criticalTasks = tasksWithUrgency.filter(t => t.urgency === 'critical').length;

  return (
    <PremiumLayout
      title="Tasks"
      subtitle={`${totalTasks} total tasks • ${completedTasks} completed${criticalTasks > 0 ? ` • ${criticalTasks} urgent` : ''}`}
      actions={
        <div className="flex items-center space-x-2">
          <select
            value={filters.origin}
            onChange={(e) => setFilters((prev) => ({ ...prev, origin: e.target.value as TaskOriginFilter }))}
            className="input-premium text-sm min-w-[160px]"
            title="Filter by task source"
          >
            <option value="all">All Tasks</option>
            <option value="human">Human Generated</option>
            <option value="agent">AI Generated</option>
          </select>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-secondary text-sm px-4 py-2 ${showFilters ? 'bg-indigo-100 text-indigo-700' : ''}`}
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </button>
          <button
            onClick={() => {
              resetForm();
              setEditingTask(null);
              setShowModal(true);
            }}
            className="btn-primary text-sm px-4 py-2"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            New Task
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Status filter tabs – always visible for quick tracking */}
        <div className="premium-card p-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-2">Status:</span>
            {[
              { value: 'all', label: 'All', count: tasks.length },
              { value: 'pending', label: 'Pending', count: tasks.filter(t => t.status === 'pending').length },
              { value: 'in_progress', label: 'In progress', count: tasks.filter(t => t.status === 'in_progress').length },
              { value: 'completed', label: 'Completed', count: tasks.filter(t => t.status === 'completed').length },
              { value: 'cancelled', label: 'Cancelled', count: tasks.filter(t => t.status === 'cancelled').length },
            ].map(({ value, label, count }) => (
              <button
                key={value}
                type="button"
                onClick={() => setFilters(f => ({ ...f, status: value as typeof f.status }))}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filters.status === value
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {label}
                <span className={`ml-1.5 ${filters.status === value ? 'text-indigo-200' : 'text-gray-500'}`}>
                  ({count})
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="premium-card p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-900">Filter Tasks</h3>
              <button
                onClick={() => setShowFilters(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Source
                </label>
                <select
                  value={filters.origin}
                  onChange={(e) => setFilters({ ...filters, origin: e.target.value as TaskOriginFilter })}
                  className="input-premium text-sm"
                >
                  <option value="all">All Tasks</option>
                  <option value="human">Human Generated</option>
                  <option value="agent">AI Generated</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value as any })}
                  className="input-premium text-sm"
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">⏳ Pending</option>
                  <option value="in_progress">🔄 In Progress</option>
                  <option value="completed">✅ Completed</option>
                  <option value="cancelled">❌ Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  value={filters.priority}
                  onChange={(e) => setFilters({ ...filters, priority: e.target.value as any })}
                  className="input-premium text-sm"
                >
                  <option value="all">All Priorities</option>
                  <option value="urgent">🔴 Urgent</option>
                  <option value="high">🟠 High</option>
                  <option value="medium">🟡 Medium</option>
                  <option value="low">⚪ Low</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Urgency
                </label>
                <select
                  value={filters.urgency}
                  onChange={(e) => setFilters({ ...filters, urgency: e.target.value as any })}
                  className="input-premium text-sm"
                >
                  <option value="all">All Urgency</option>
                  <option value="critical">🔴 Critical</option>
                  <option value="high">🟠 High</option>
                  <option value="medium">🟡 Medium</option>
                  <option value="low">⚪ Low</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Search
                </label>
                <input
                  type="text"
                  value={filters.searchTerm}
                  onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
                  className="input-premium text-sm"
                  placeholder="Search tasks..."
                />
              </div>
            </div>
            {(filters.origin !== 'all' || filters.status !== 'all' || filters.priority !== 'all' || filters.urgency !== 'all' || filters.searchTerm) && (
              <div className="mt-4 flex items-center space-x-2">
                <button
                  onClick={() => setFilters({ origin: 'all', status: 'all', priority: 'all', urgency: 'all', searchTerm: '' })}
                  className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  Clear Filters
                </button>
                <span className="text-sm text-gray-500">
                  Showing {filteredTasks.length} of {tasks.length} tasks
                </span>
              </div>
            )}
          </div>
        )}

        {/* Main Content */}
        {selectedClient ? (
          // Client Detail View
          <div className="space-y-4">
            <div className="premium-card p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setSelectedClient(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                  </button>
                  <div className="flex items-center space-x-2">
                    {getClientIcon(selectedClient.type)}
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">{selectedClient.name}</h2>
                      <p className="text-xs text-gray-500 capitalize">
                        {selectedClient.type} {selectedClient.leadStage ? `• ${selectedClient.leadStage.replace('_', ' ')}` : ''} • {selectedClient.taskCount} tasks
                      </p>
                    </div>
                  </div>
                  {/* View Lead/Account button */}
                  {selectedClient.entityId > 0 && (
                    <button
                      onClick={() => navigate(selectedClient.type === 'lead' ? `/leads/${selectedClient.entityId}` : `/accounts/${selectedClient.entityId}`)}
                      className="ml-4 flex items-center px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                    >
                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 mr-1" />
                      View {selectedClient.type === 'lead' ? 'Lead' : 'Account'}
                    </button>
                  )}
                </div>
                <button
                  onClick={() => {
                    const newFormData: any = {
                      title: '',
                      description: '',
                      status: 'pending',
                      priority: 'medium',
                      due_date: '',
                      lead_id: '',
                      account_id: '',
                    };
                    if (selectedClient.type === 'account') {
                      newFormData.account_id = String(selectedClient.entityId);
                    } else if (selectedClient.type === 'lead') {
                      newFormData.lead_id = String(selectedClient.entityId);
                    }
                    setFormData(newFormData);
                    setEditingTask(null);
                    setShowModal(true);
                  }}
                  className="btn-primary text-sm px-4 py-2"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Task
                </button>
              </div>
            </div>

            {/* Tasks Timeline */}
            <div className="premium-card overflow-hidden">
              <div className="divide-y divide-gray-100">
                {selectedClientTasks.filter(task => !isSystemClosePlanTask(task.title)).map((task: any) => {
                  const isHighlighted = highlightedTaskId === task.id;
                  return (
                    <div 
                      key={task.id}
                      ref={(el) => {
                        if (el) taskRefs.current[task.id] = el;
                      }}
                      className={`p-6 hover:bg-gradient-to-r hover:from-gray-50 hover:to-transparent transition-all group border-l-4 ${
                        isHighlighted 
                          ? 'border-indigo-500 bg-indigo-100 shadow-lg ring-2 ring-indigo-300 ring-opacity-50' 
                          : task.urgency === 'critical' ? 'border-red-500 bg-red-50/30' :
                            task.urgency === 'high' ? 'border-orange-500 bg-orange-50/30' :
                            task.urgency === 'medium' ? 'border-yellow-500 bg-yellow-50/30' :
                            'border-gray-300 bg-gray-50/30'
                      }`}
                    >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className="flex-shrink-0 mt-1">
                          {getStatusIcon(task.status)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <h3 className="text-lg font-semibold text-gray-900">{task.title}</h3>
                              {task.urgency && (
                                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${getUrgencyColor(task.urgency)}`}>
                                  {task.urgency.toUpperCase()}
                                </span>
                              )}
                            </div>
                            <span className="text-sm text-gray-500 ml-4">
                              {task.status === 'completed' && task.completed_at ? (
                                <span className="text-green-700">
                                  Completed {new Date(task.completed_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                  })}
                                </span>
                              ) : task.due_date ? (
                                <>
                                  Due {new Date(task.due_date).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                  })}
                                  {new Date(task.due_date) < new Date() && (
                                    <span className="ml-2 text-red-600 font-semibold">(Overdue)</span>
                                  )}
                                </>
                              ) : (
                                new Date(task.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })
                              )}
                            </span>
                          </div>
                          <div className="flex items-center space-x-3 mb-2">
                            <span className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold border ${getPriorityColor(task.priority)}`}>
                              {task.priority || 'low'} priority
                            </span>
                            <span className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold border ${getOwnerTypeMeta(task.owner_type).className}`}>
                              {getOwnerTypeMeta(task.owner_type).label}
                            </span>
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              {task.status.replace('_', ' ')}
                            </span>
                            {task.urgencyReason && (
                              <span className="text-xs text-gray-500 italic">{task.urgencyReason}</span>
                            )}
                          </div>
                          {task.description && (
                            <p className="text-sm text-gray-700 mt-2">{task.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        {taskNotificationMap.get(task.id)?.length ? (
                          <button
                            onClick={() => handleClearTaskNotifications(task.id)}
                            className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
                            title="Clear task notifications"
                          >
                            <TrashIcon className="h-4 w-4" />
                            <span className="ml-1">Clear</span>
                          </button>
                        ) : null}
                        <button
                          onClick={() => handleEdit(task)}
                          className="inline-flex items-center px-3 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors border border-indigo-200"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                          <span className="ml-1">Edit</span>
                        </button>
                        <button
                          onClick={() => handleDelete(task.id)}
                          className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors border border-gray-200"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                          <span className="ml-1">Delete</span>
                        </button>
                      </div>
                    </div>
                    {isHighlighted && (
                      <div className="mt-2 flex items-center justify-between text-xs text-indigo-600 font-medium">
                        <div className="flex items-center">
                          <InformationCircleIcon className="h-4 w-4 mr-1" />
                          Navigated from notification
                        </div>
                        {notificationIdToClear && (
                          <button
                            type="button"
                            onClick={handleClearNotification}
                            className="inline-flex items-center text-red-600 hover:text-red-800 font-medium"
                          >
                            <TrashIcon className="h-3.5 w-3.5 mr-1" />
                            Clear notification
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
                {selectedClientTasks.length === 0 && (
                  <div className="p-12 text-center">
                    <p className="text-gray-500">No tasks for this client</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          // Client List View
          <div className="premium-card overflow-hidden">
            <div className="divide-y divide-gray-200">
              {clientGroups.map((client) => (
                <div
                  key={client.id}
                  onClick={() => setSelectedClient(client)}
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        {getClientIcon(client.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-base font-semibold text-gray-900 truncate">{client.name}</h3>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 capitalize font-medium">
                            {client.type}
                          </span>
                          {client.leadStage && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 capitalize font-medium">
                              {client.leadStage.replace('_', ' ')}
                            </span>
                          )}
                        </div>
                        {client.tasks.length > 0 && (
                          <div className="mt-1.5 flex items-center space-x-4 text-sm text-gray-500">
                            <span className="flex items-center space-x-1">
                              {getStatusIcon(client.tasks[0].status)}
                              <span className="truncate max-w-xs">{client.tasks[0].title}</span>
                            </span>
                            {client.tasks[0].urgency && (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${getUrgencyColor(client.tasks[0].urgency)}`}>
                                {client.tasks[0].urgency.toUpperCase()}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-6 flex-shrink-0">
                      <div className="text-right">
                        <div className="text-sm font-semibold text-gray-900">{client.taskCount}</div>
                        <div className="text-xs text-gray-500">tasks</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-700">
                          {new Date(client.lastTaskDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(client.lastTaskDate).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      </div>
                      <div>
                        <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {clientGroups.length === 0 && (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CheckCircleIcon className="h-8 w-8 text-indigo-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No tasks found</h3>
                <p className="text-gray-500 mb-4">Try adjusting your filters or create your first task.</p>
                <button
                  onClick={() => {
                    resetForm();
                    setEditingTask(null);
                    setShowModal(true);
                  }}
                  className="btn-primary"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Create Task
                </button>
              </div>
            )}
          </div>
        )}

        {/* Task Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-4 pb-4 px-4">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all fade-in">
              <div className="p-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">
                    {editingTask ? 'Edit Task' : 'New Task'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {editingTask ? 'Update task information' : 'Create a follow-up task'}
                  </p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Task Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="input-premium"
                      placeholder="Enter task title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={4}
                      className="input-premium"
                      placeholder="Add task details..."
                    />
                  </div>
                  {!selectedClient && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Link to Lead (Optional)
                        </label>
                        <select
                          value={formData.lead_id}
                          onChange={(e) => setFormData({ ...formData, lead_id: e.target.value, account_id: e.target.value ? '' : formData.account_id })}
                          className="input-premium"
                        >
                          <option value="">None</option>
                          {leads.map((lead) => {
                            // Get account name if available
                            const account = accounts.find(a => a.id === lead.account_id);
                            const displayName = account?.name || lead.project_name || lead.brand || lead.name;
                            return (
                              <option key={lead.id} value={lead.id}>
                                {displayName} {(() => {
                                  const contactName = lead.first_name && lead.last_name
                                    ? `${lead.first_name} ${lead.last_name}`
                                    : lead.first_name || lead.lead_name || '';
                                  return contactName ? `(${contactName})` : '';
                                })()} - {lead.stage}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Link to Account (Optional)
                        </label>
                        <select
                          value={formData.account_id}
                          onChange={(e) => setFormData({ ...formData, account_id: e.target.value, lead_id: e.target.value ? '' : formData.lead_id })}
                          className="input-premium"
                          disabled={!!formData.lead_id}
                        >
                          <option value="">None</option>
                          {accounts.map((account) => (
                            <option key={account.id} value={account.id}>
                              {account.name}
                            </option>
                          ))}
                        </select>
                        {formData.lead_id && (
                          <p className="text-xs text-gray-500 mt-1">Cannot link to account when linked to lead</p>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="input-premium"
                      >
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Priority
                      </label>
                      <select
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                        className="input-premium"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <CalendarIcon className="h-4 w-4 inline mr-1" />
                        Due Date
                      </label>
                      <input
                        type="date"
                        value={formData.due_date}
                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                        className="input-premium"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <CheckCircleIcon className="h-4 w-4 inline mr-1" />
                        Completion Date
                      </label>
                      <input
                        type="date"
                        value={formData.completed_at}
                        onChange={(e) => setFormData({ ...formData, completed_at: e.target.value })}
                        className="input-premium"
                        disabled={formData.status !== 'completed'}
                        title={formData.status !== 'completed' ? 'Set status to Completed to set completion date' : ''}
                      />
                      {formData.status !== 'completed' && (
                        <p className="text-xs text-gray-500 mt-1">Set status to Completed to use</p>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-3 pt-6 border-t border-gray-200 mt-6">
                    <button
                      type="submit"
                      disabled={createMutation.isPending || updateMutation.isPending}
                      className="btn-primary flex-1"
                    >
                      {createMutation.isPending || updateMutation.isPending
                        ? 'Saving...'
                        : editingTask
                        ? 'Update Task'
                        : 'Create Task'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        setEditingTask(null);
                        resetForm();
                      }}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </PremiumLayout>
  );
};

export default Tasks;
